import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaArrowRight, FaCopy, FaRegCheckCircle, FaLaptopCode, 
  FaPlay, FaPalette, FaBug, FaCloudUploadAlt, FaWrench, FaCheck, FaLink,
  FaShoppingCart, FaCalendarAlt, FaThLarge, FaImage, FaUserCircle, 
  FaGlobe, FaComments, FaBookOpen
} from 'react-icons/fa';
import { useTheme } from '../ThemeContext';

export default function WebPlatformsDetail() {
  useTheme();

  // 1. "Website Blueprint Builder" (Plain-English Configurator)
  const [siteArchetype, setSiteArchetype] = useState('ecommerce'); // ecommerce, saas, booking, brand
  const [addOnChat, setAddOnChat] = useState(false);
  const [addOnLang, setAddOnLang] = useState(false);
  const [addOnBlog, setAddOnBlog] = useState(false);
  const [addOnAuth, setAddOnAuth] = useState(false);

  // Selector states inside mockup
  const [mockCartCount, setMockCartCount] = useState(2);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [copiedSpec, setCopiedSpec] = useState(false);

  // 2. Interactive Journey Stages (Common-man terminology)
  // Stages: 'designing' | 'development' | 'testing' | 'deployment' | 'support'
  const [journeyStage, setJourneyStage] = useState('designing');

  // Journey stage sub-states
  // Designing: Spacing and primary hue
  const [designSpacing, setDesignSpacing] = useState('tight'); // tight, cozy, loose
  const [designTheme, setDesignTheme] = useState('cyan'); // cyan, emerald, indigo

  // Development: Compiling animation
  const [devCompiling, setDevCompiling] = useState(false);
  const [devCompiled, setDevCompiled] = useState(false);
  const [devOutput, setDevOutput] = useState('');

  // Testing: Running test logs
  const [testsRunning, setTestsRunning] = useState(false);
  const [testsPassed, setTestsPassed] = useState(false);
  const [testLogs, setTestLogs] = useState([]);

  // Deployment: deploying animation
  const [deploying, setDeploying] = useState(false);
  const [deployed, setDeployed] = useState(false);
  const [deployLogs, setDeployLogs] = useState([]);

  // Support & Maintenance: telemetry spikes
  const [telemetryServerLoad, setTelemetryServerLoad] = useState(12); // %
  const [telemetryMemory, setTelemetryMemory] = useState(482); // MB
  const telemetryUptime = 99.998;

  // Telemetry loop for Support & Maintenance
  useEffect(() => {
    let interval;
    if (journeyStage === 'support') {
      interval = setInterval(() => {
        setTelemetryServerLoad(prev => {
          const delta = Math.floor(Math.random() * 10) - 5;
          return Math.max(5, Math.min(65, prev + delta));
        });
        setTelemetryMemory(prev => {
          const delta = Math.floor(Math.random() * 6) - 3;
          return Math.max(450, Math.min(520, prev + delta));
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [journeyStage]);

  // Handle typing/compilation simulator
  const handleCompileDev = () => {
    if (devCompiling || devCompiled) return;
    setDevCompiling(true);
    setDevOutput('Initializing design rules layout...\n');
    
    setTimeout(() => {
      setDevOutput(prev => prev + '> Reading selected blueprints configuration...\n');
    }, 400);

    setTimeout(() => {
      setDevOutput(prev => prev + '> Initializing secure Payment SDK endpoints (Stripe)...\n');
    }, 900);

    setTimeout(() => {
      setDevOutput(prev => prev + '> Building database schemas and API controllers...\n');
    }, 1400);

    setTimeout(() => {
      setDevOutput(prev => prev + '✔ Platform modules assembled successfully. Ready for deployment!\n');
      setDevCompiling(false);
      setDevCompiled(true);
    }, 2000);
  };

  const resetDevMode = () => {
    setDevCompiled(false);
    setDevOutput('');
  };

  // Handle testing suite runner
  const handleRunTests = () => {
    if (testsRunning) return;
    setTestsRunning(true);
    setTestsPassed(false);
    setTestLogs(['$ npm run test --quality-guarantee']);

    const steps = [
      { text: '✓ Security check: SQL Injection inputs validation - PASS (152ms)', delay: 400 },
      { text: '✓ Payment gateway check: Sandbox checkout loop - PASS (390ms)', delay: 1000 },
      { text: '✓ Load speed check: Mobile layout load under 1.5s - PASS (210ms)', delay: 1600 },
      { text: '✓ Browser compatibility check: Tested Chrome, Safari, Firefox - PASS (80ms)', delay: 2100 },
      { text: '\nIntegrations: 4 passed, 4 total\nQuality Standards: 100% STABLE & PRODUCTION READY.', delay: 2600 }
    ];

    steps.forEach((step) => {
      setTimeout(() => {
        setTestLogs(prev => [...prev, step.text]);
        if (step.text.includes('100% STABLE')) {
          setTestsRunning(false);
          setTestsPassed(true);
        }
      }, step.delay);
    });
  };

  // Handle deployment logger
  const handleDeployToStaging = () => {
    if (deploying) return;
    setDeploying(true);
    setDeployed(false);
    setDeployLogs(['$ web-deploy --production']);

    const steps = [
      { text: '[Handover Prep] Building production optimized bundles...', delay: 300 },
      { text: '[CDN routing] Synced layouts on 124 international servers...', delay: 800 },
      { text: '[SEO indexing] Initiated sitemaps compilation indices...', delay: 1400 },
      { text: '[SSL certificates] Registered dynamic secure link certificates...', delay: 1900 },
      { text: '✔ Live Preview link configured: https://staging.bccweb.dev', delay: 2400 }
    ];

    steps.forEach((step) => {
      setTimeout(() => {
        setDeployLogs(prev => [...prev, step.text]);
        if (step.text.includes('Preview link configured')) {
          setDeploying(false);
          setDeployed(true);
        }
      }, step.delay);
    });
  };

  const handleCopySpec = () => {
    const specSummary = `BCC Website Blueprint spec:
- Archetype: ${siteArchetype.toUpperCase()}
- Add-ons selected: ${[
  addOnAuth ? 'Customer Portals' : '',
  addOnChat ? 'Live Chat Integration' : '',
  addOnLang ? 'Multi-language support' : '',
  addOnBlog ? 'SEO Blog module' : ''
].filter(Boolean).join(', ') || 'None'}`;
    navigator.clipboard.writeText(specSummary);
    setCopiedSpec(true);
  };

  useEffect(() => {
    if (copiedSpec) {
      const timer = setTimeout(() => setCopiedSpec(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedSpec]);

  return (
    <div className="min-h-screen overflow-x-hidden transition-all duration-300" style={{ background: 'var(--bg-primary)' }}>
      
      {/* ── HERO SECTION ── */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden" 
               style={{ 
                 background: 'var(--bg-card)',
                 borderBottom: '1px solid var(--section-divider)' 
               }}>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[140px] opacity-10 pointer-events-none bg-blue-500" />
        
        <div className="section-container relative z-10 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
               style={{ 
                 background: 'var(--accent-soft)',
                 border: '1px solid var(--border-color)' 
               }}>
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            <span className="text-xs font-bold uppercase tracking-wider text-blue-500">Core Services</span>
          </div>

          <h1 className="display-lg mb-6 leading-tight font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>
            State-of-the-Art <span className="blue-gradient">Web Platforms</span>.
          </h1>
          
          <p className="text-lg leading-relaxed mb-10 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            We design, develop, and deploy enterprise-grade web applications. We utilize next-generation static caching, modern relational schemas, and highly polished user interaction components.
          </p>

          <div className="flex justify-center gap-4">
            <Link to="/contactus" className="btn-primary">
              Start Your Spec Sheet <FaArrowRight size={11} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── WHAT WE BUILD ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-primary)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-6xl">
          <div className="text-center mb-20">
            <p className="eyebrow mb-2">Our Expertise</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>What We Build</h2>
            <p className="subheadline max-w-2xl mx-auto">
              From digital storefronts to enterprise portals — we craft high-performance platforms tailored exactly to your business goals.
            </p>
          </div>

          <div className="space-y-28">

            {/* Row 1 — E-Commerce: image left, text right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="rounded-3xl overflow-hidden shadow-2xl border" style={{ borderColor: 'var(--border-color)' }}>
                <img src="/ecommerce_preview.png" alt="E-Commerce Store Preview" className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-blue-500 mb-4">
                  <span className="w-4 h-px bg-blue-500 inline-block" /> E-Commerce
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Online Stores That Sell
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  We build full-featured online stores — from product pages and smart filters to cart flows and secure checkout. Whether you sell 10 products or 10,000, your store is engineered to convert browsers into buyers.
                </p>
                <ul className="space-y-2 mb-8">
                  {['Product catalogue with filtering & search', 'Stripe, Razorpay & PayPal integrations', 'Real-time inventory & order management', 'Discount codes, wishlists & reviews'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <FaCheck size={10} className="text-blue-500 mt-1 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {['Cart & Checkout', 'Stripe Payments', 'Product Catalog', 'Order Tracking'].map(tag => (
                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 2 — SaaS: text left, image right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-indigo-500 mb-4">
                  <span className="w-4 h-px bg-indigo-500 inline-block" /> SaaS & Web Apps
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Dashboards & Software Products
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  We design and build data-driven web applications and SaaS portals with user auth, subscription billing, real-time analytics, and admin controls — built to scale from day one.
                </p>
                <ul className="space-y-2 mb-8">
                  {['Role-based login & authentication', 'Subscription billing with Stripe', 'Custom analytics dashboards & charts', 'REST APIs and third-party integrations'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <FaCheck size={10} className="text-indigo-500 mt-1 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {['User Auth', 'Admin Dashboard', 'REST APIs', 'Subscription Billing'].map(tag => (
                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2 rounded-3xl overflow-hidden shadow-2xl border" style={{ borderColor: 'var(--border-color)' }}>
                <img src="/saas_preview.png" alt="SaaS Dashboard Preview" className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Row 3 — Business Websites: image left, text right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="rounded-3xl overflow-hidden shadow-2xl border" style={{ borderColor: 'var(--border-color)' }}>
                <img src="/business_preview.png" alt="Business Website Preview" className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-emerald-500 mb-4">
                  <span className="w-4 h-px bg-emerald-500 inline-block" /> Business Websites
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Your Brand on the Web, Done Right
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  A polished, fast-loading website that makes your company look credible and converts visitors into leads. Designed to rank on Google, load in under a second, and work flawlessly on every device.
                </p>
                <ul className="space-y-2 mb-8">
                  {['Multi-page layout with contact & about sections', 'Built-in SEO meta, sitemaps & analytics', 'Lead capture forms with CRM integration', 'Fully responsive across all devices'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <FaCheck size={10} className="text-emerald-500 mt-1 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {['SEO Optimised', 'Lead Forms', 'CMS Integration', 'Mobile First'].map(tag => (
                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 4 — Booking: text left, image right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" style={{ alignItems: 'center' }}>
              <div className="order-2 lg:order-1">
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-amber-500 mb-4">
                  <span className="w-4 h-px bg-amber-500 inline-block" /> Booking Platforms
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Let Clients Book Themselves
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Smart scheduling systems for clinics, salons, coaches, and consultants. Once set up, clients self-book from your live calendar and automated emails handle all confirmations — no back-and-forth needed.
                </p>
                <ul className="space-y-2 mb-8">
                  {['Live availability calendar', 'Automated email & SMS reminders', 'Service menu with duration & pricing', 'Client profiles & booking history'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <FaCheck size={10} className="text-amber-500 mt-1 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {['Live Calendar', 'Email Reminders', 'Slot Management', 'Client Profiles'].map(tag => (
                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2 flex items-center justify-center">
                <div className="rounded-3xl overflow-hidden shadow-2xl border w-full" style={{ borderColor: 'var(--border-color)' }}>
                  <img src="/booking_preview.png" alt="Booking Platform Preview" className="w-full object-cover block" />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── THE WEBSITE BLUEPRINT BUILDER ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-surface)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-5xl">
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Build Your Platform</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Interactive Specs Blueprint</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Select your business goals model below to preview your website layout and choose add-on configurations in real-time.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Left Panel: Archetypes & Options Selector (5 columns) */}
            <div className="lg:col-span-5 flex flex-col justify-between border rounded-3xl p-6 shadow-xl"
                 style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <div>
                <h3 className="text-md font-bold mb-4" style={{ color: 'var(--text-primary)' }}>1. What type of platform do you need?</h3>
                
                {/* Archetype Cards */}
                <div className="space-y-3 mb-8">
                  {/* Option A: E-Commerce */}
                  <button 
                    onClick={() => setSiteArchetype('ecommerce')}
                    className={`w-full text-left p-3.5 rounded-xl border flex items-center gap-4 transition-all duration-200 ${
                      siteArchetype === 'ecommerce' ? 'bg-blue-600/10 border-blue-600/40 text-blue-500 scale-[1.01]' : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                      <FaShoppingCart size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>E-Commerce Store</h4>
                      <p className="text-[10px] text-zinc-400 mt-0.5">Sell physical products or digital assets with checkout integrations.</p>
                    </div>
                  </button>

                  {/* Option B: SaaS / Web App */}
                  <button 
                    onClick={() => setSiteArchetype('saas')}
                    className={`w-full text-left p-3.5 rounded-xl border flex items-center gap-4 transition-all duration-200 ${
                      siteArchetype === 'saas' ? 'bg-blue-600/10 border-blue-600/40 text-blue-500 scale-[1.01]' : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                      <FaThLarge size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>SaaS & Web App</h4>
                      <p className="text-[10px] text-zinc-400 mt-0.5">Interactive software dashboard portal with user logins & billing.</p>
                    </div>
                  </button>

                  {/* Option C: Appointment Scheduling */}
                  <button 
                    onClick={() => setSiteArchetype('booking')}
                    className={`w-full text-left p-3.5 rounded-xl border flex items-center gap-4 transition-all duration-200 ${
                      siteArchetype === 'booking' ? 'bg-blue-600/10 border-blue-600/40 text-blue-500 scale-[1.01]' : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                      <FaCalendarAlt size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Service Bookings</h4>
                      <p className="text-[10px] text-zinc-400 mt-0.5">Collect client scheduling entries, reservations, and reminders.</p>
                    </div>
                  </button>

                  {/* Option D: Brand Showcase */}
                  <button 
                    onClick={() => setSiteArchetype('brand')}
                    className={`w-full text-left p-3.5 rounded-xl border flex items-center gap-4 transition-all duration-200 ${
                      siteArchetype === 'brand' ? 'bg-blue-600/10 border-blue-600/40 text-blue-500 scale-[1.01]' : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                      <FaImage size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Premium Brand Showcase</h4>
                      <p className="text-[10px] text-zinc-400 mt-0.5">Highly interactive layout with immersive visual portfolio highlights.</p>
                    </div>
                  </button>
                </div>

                {/* Add-ons list */}
                <h3 className="text-md font-bold mb-4" style={{ color: 'var(--text-primary)' }}>2. Choose client-facing add-ons</h3>
                <div className="grid grid-cols-2 gap-3">
                  
                  {/* Add-on: Portals */}
                  <button 
                    onClick={() => setAddOnAuth(!addOnAuth)}
                    className={`p-3 border rounded-xl flex flex-col items-center text-center gap-2 transition-all duration-200 ${
                      addOnAuth ? 'bg-blue-600/5 border-blue-500/30 text-blue-500' : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5 text-zinc-500'
                    }`}
                  >
                    <FaUserCircle size={16} />
                    <span className="text-[10px] uppercase font-bold tracking-wider">User Portals</span>
                  </button>

                  {/* Add-on: Multi-language */}
                  <button 
                    onClick={() => setAddOnLang(!addOnLang)}
                    className={`p-3 border rounded-xl flex flex-col items-center text-center gap-2 transition-all duration-200 ${
                      addOnLang ? 'bg-blue-600/5 border-blue-500/30 text-blue-500' : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5 text-zinc-500'
                    }`}
                  >
                    <FaGlobe size={16} />
                    <span className="text-[10px] uppercase font-bold tracking-wider">Multi-Language</span>
                  </button>

                  {/* Add-on: Chatbot */}
                  <button 
                    onClick={() => setAddOnChat(!addOnChat)}
                    className={`p-3 border rounded-xl flex flex-col items-center text-center gap-2 transition-all duration-200 ${
                      addOnChat ? 'bg-blue-600/5 border-blue-500/30 text-blue-500' : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5 text-zinc-500'
                    }`}
                  >
                    <FaComments size={16} />
                    <span className="text-[10px] uppercase font-bold tracking-wider">Live Chatbot</span>
                  </button>

                  {/* Add-on: SEO Blog */}
                  <button 
                    onClick={() => setAddOnBlog(!addOnBlog)}
                    className={`p-3 border rounded-xl flex flex-col items-center text-center gap-2 transition-all duration-200 ${
                      addOnBlog ? 'bg-blue-600/5 border-blue-500/30 text-blue-500' : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5 text-zinc-500'
                    }`}
                  >
                    <FaBookOpen size={16} />
                    <span className="text-[10px] uppercase font-bold tracking-wider">SEO Blog Hub</span>
                  </button>

                </div>
              </div>

              {/* Action proposal CTA */}
              <div className="mt-8">
                <button 
                  onClick={handleCopySpec}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs tracking-wider transition-all duration-200"
                >
                  {copiedSpec ? <FaRegCheckCircle size={13} /> : <FaCopy size={12} />}
                  {copiedSpec ? "CONFIG BLUEPRINT COPIED!" : "COPY CONFIG SPEC & CONTACT"}
                </button>
              </div>

            </div>

            {/* Right Panel: Blueprint mock screen (7 columns) */}
            <div className="lg:col-span-7 flex flex-col justify-stretch">
              <div className="w-full h-full border rounded-3xl p-6 flex flex-col justify-between shadow-xl"
                   style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                
                <div>
                  <div className="flex items-center justify-between pb-3 border-b mb-6" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                      <span className="text-[10px] font-mono uppercase font-bold tracking-widest text-zinc-400">
                        Visual Page Blueprint Mockup
                      </span>
                    </div>
                    <span className="text-[9px] uppercase font-bold border px-2.5 py-0.5 rounded tracking-wide text-zinc-500" style={{ borderColor: 'var(--border-color)' }}>
                      Desktop View
                    </span>
                  </div>

                  {/* Browser Mock Wrapper */}
                  <div className="w-full border rounded-2xl overflow-hidden relative" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                    
                    {/* Mock Browser URL Bar */}
                    <div className="bg-zinc-100 dark:bg-zinc-800/80 px-4 py-2 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
                      <div className="flex gap-1 items-center shrink-0">
                        <span className="w-2 h-2 rounded-full bg-zinc-400" />
                        <span className="w-2 h-2 rounded-full bg-zinc-400" />
                        <span className="w-2 h-2 rounded-full bg-zinc-400" />
                      </div>
                      
                      {/* URL Box */}
                      <div className="flex-grow max-w-sm mx-auto h-5 bg-white dark:bg-zinc-900 rounded text-[9px] text-zinc-400 flex items-center justify-between px-2 font-mono">
                        <span>https://mycompany.live</span>
                        {addOnLang && <span className="text-[8px] font-bold text-blue-500">🌐 EN | ES</span>}
                      </div>

                      <div className="w-4" />
                    </div>

                    {/* Mock Page Content Body */}
                    <div className="p-5 min-h-[220px] text-left">
                      
                      {/* Mock Header Row */}
                      <div className="flex items-center justify-between mb-6 pb-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                        <div className="w-12 h-3.5 bg-blue-500/20 rounded" />
                        
                        <div className="flex items-center gap-3">
                          <div className="h-2 w-8 bg-zinc-300 dark:bg-zinc-700 rounded-full" />
                          <div className="h-2 w-8 bg-zinc-300 dark:bg-zinc-700 rounded-full" />
                          
                          {addOnAuth && (
                            <div className="flex items-center text-blue-500 gap-1 text-[11px] font-bold shrink-0">
                              <FaUserCircle size={12} /> Portal
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ── MOCK SCREEN CONTENT: E-COMMERCE ── */}
                      {siteArchetype === 'ecommerce' && (
                        <div className="space-y-4">
                          <div className="flex justify-between items-start gap-4">
                            <div className="text-left">
                              <span className="text-[10px] font-black uppercase text-blue-500 tracking-wider">AuraStore ©</span>
                              <h4 className="text-sm font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>Summer Drops '26</h4>
                            </div>
                            
                            {/* Shopping cart widget */}
                            <button 
                              onClick={() => setMockCartCount(0)}
                              className="flex items-center gap-1.5 p-1.5 px-3 rounded-lg border text-blue-500 bg-blue-500/5 select-none hover:bg-blue-500/10 active:scale-95 transition-all text-[9px] font-bold font-mono" 
                              style={{ borderColor: 'var(--border-color)' }}
                              title="Click to empty cart"
                            >
                              <FaShoppingCart size={11} /> 
                              <span>{mockCartCount} items</span>
                            </button>
                          </div>

                          {/* Mock Product Grid */}
                          <div className="grid grid-cols-2 gap-3 pt-1">
                            <div 
                              onClick={() => setMockCartCount(prev => prev + 1)}
                              className="p-3 border rounded-xl text-left bg-white dark:bg-zinc-900 shadow-sm cursor-pointer hover:border-blue-550 border-solid transition-all hover:-translate-y-[2px] group"
                              style={{ borderColor: 'var(--border-color)' }}
                            >
                              <div className="aspect-video bg-gradient-to-tr from-blue-500/20 to-indigo-500/10 rounded-lg flex items-center justify-center mb-2 overflow-hidden relative">
                                <span className="text-[8px] uppercase tracking-widest text-blue-500 font-black">AERO-FIT</span>
                                <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-[9px] font-black" style={{ color: 'var(--text-primary)' }}>AeroFit Sneaker</span>
                                <span className="text-[8px] font-mono text-zinc-500">★ 4.9</span>
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-[9px] font-bold text-zinc-650 dark:text-zinc-400">$128.00</span>
                                <span className="text-[8px] uppercase font-bold text-blue-500 group-hover:underline">Add +</span>
                              </div>
                            </div>

                            <div className="p-3 border rounded-xl text-left bg-white dark:bg-zinc-900 shadow-sm cursor-pointer hover:border-blue-555 border-dashed transition-all hover:-translate-y-[2px] group"
                              onClick={() => setMockCartCount(prev => prev + 1)}
                              style={{ borderColor: 'var(--border-color)' }}
                            >
                              <div className="aspect-video bg-gradient-to-tr from-amber-500/20 to-red-500/10 rounded-lg flex items-center justify-center mb-2 overflow-hidden relative">
                                <span className="text-[8px] uppercase tracking-widest text-amber-500 font-black">PRO-BUDS</span>
                                <div className="absolute inset-0 bg-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-[9px] font-black" style={{ color: 'var(--text-primary)' }}>Pro Audio Buds</span>
                                <span className="text-[8px] font-mono text-zinc-500">★ 4.8</span>
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-[9px] font-bold text-zinc-655 dark:text-zinc-400">$189.00</span>
                                <span className="text-[8px] uppercase font-bold text-blue-500 group-hover:underline">Add +</span>
                              </div>
                            </div>
                          </div>

                          {/* Stripe Checkout badge */}
                          <div className="p-2.5 border rounded-xl flex items-center justify-between bg-white dark:bg-zinc-900" style={{ borderColor: 'var(--border-color)' }}>
                            <div className="flex items-center gap-1.5">
                              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                              <span className="text-[9px] font-bold text-zinc-500">Stripe Checkout Connected</span>
                            </div>
                            <span className="text-[8px] font-mono font-bold text-blue-500">SECURE CONNECT</span>
                          </div>
                        </div>
                      )}

                      {/* ── MOCK SCREEN CONTENT: SAAS / DASHBOARD ── */}
                      {siteArchetype === 'saas' && (
                        <div className="flex gap-3 h-full min-h-[170px]">
                          {/* Sidebar menu */}
                          <div className="w-1/4 border-r pr-2 space-y-1.5 text-left shrink-0" style={{ borderColor: 'var(--border-color)' }}>
                            <div className="text-[8px] font-black tracking-wider text-blue-500 uppercase px-1">WORKSPACE</div>
                            <div className="p-1 bg-blue-500/5 text-blue-500 rounded text-[9px] font-black cursor-pointer">📊 Dashboard</div>
                            <div className="p-1 hover:bg-zinc-800/5 dark:hover:bg-zinc-800/20 rounded text-[9px] font-bold text-zinc-400 cursor-pointer">👥 Team</div>
                            <div className="p-1 hover:bg-zinc-800/5 dark:hover:bg-zinc-800/20 rounded text-[9px] font-bold text-zinc-400 cursor-pointer">💳 Billing</div>
                            <div className="p-1 hover:bg-zinc-800/5 dark:hover:bg-zinc-800/20 rounded text-[9px] font-bold text-zinc-400 cursor-pointer">⚙ Settings</div>
                          </div>

                          {/* Dashboard body */}
                          <div className="flex-1 space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-black text-zinc-800 dark:text-zinc-205">Main Control Center</span>
                              <div className="h-2 w-12 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
                            </div>

                            {/* SaaS Metrics block */}
                            <div className="grid grid-cols-3 gap-2">
                              <div className="p-2 border rounded-lg bg-white dark:bg-zinc-900 text-left cursor-default shadow-sm" style={{ borderColor: 'var(--border-color)' }}>
                                <span className="text-[6.5px] uppercase tracking-wider text-zinc-400 font-bold block">MRR</span>
                                <span className="text-[10px] font-mono font-black text-zinc-700 dark:text-zinc-300">$24.8K</span>
                              </div>
                              <div className="p-2 border rounded-lg bg-white dark:bg-zinc-900 text-left cursor-default shadow-sm" style={{ borderColor: 'var(--border-color)' }}>
                                <span className="text-[6.5px] uppercase tracking-wider text-zinc-400 font-bold block">Sessions</span>
                                <span className="text-[10px] font-mono font-black text-emerald-500">+14%</span>
                              </div>
                              <div className="p-2 border rounded-lg bg-white dark:bg-zinc-900 text-left cursor-default shadow-sm relative overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                                <span className="text-[6.5px] uppercase tracking-wider text-zinc-400 font-bold block">Server</span>
                                <span className="text-[9px] font-mono font-black text-blue-500 font-bold">14 ms</span>
                              </div>
                            </div>

                            {/* SVG Chart */}
                            <div className="p-1.5 border rounded-lg bg-white dark:bg-zinc-900 h-14 relative" style={{ borderColor: 'var(--border-color)' }}>
                              <svg className="w-full h-full" viewBox="0 0 100 20" preserveAspectRatio="none">
                                <defs>
                                  <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.25" />
                                    <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0" />
                                  </linearGradient>
                                </defs>
                                <path d="M0,20 L0,15 Q15,4 30,12 T60,5 T90,14 T100,6 L100,20 Z" fill="url(#chartGrad)" />
                                <path d="M0,15 Q15,4 30,12 T60,5 T90,14 T100,6" fill="none" stroke="rgb(59, 130, 246)" strokeWidth="1" />
                                <circle cx="30" cy="12" r="1.5" fill="rgb(59, 130, 246)" />
                                <circle cx="60" cy="5" r="1.5" fill="rgb(59, 130, 246)" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ── MOCK SCREEN CONTENT: SERVICE BOOKING ── */}
                      {siteArchetype === 'booking' && (
                        <div className="space-y-4">
                          <div className="text-left">
                            <span className="text-[9px] font-black uppercase text-blue-500 tracking-wider">Appointment Desk</span>
                            <h4 className="text-sm font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>Select Strategy Session</h4>
                          </div>

                          <div className="flex gap-3">
                            {/* Calendar selection grid */}
                            <div className="flex-grow grid grid-cols-5 gap-1.5 p-2 border rounded-xl bg-white dark:bg-zinc-900 shadow-sm" style={{ borderColor: 'var(--border-color)' }}>
                              {['Mon 19', 'Tue 20', 'Wed 21', 'Thu 22', 'Fri 23'].map((day, idx) => {
                                const [dayName, dateNum] = day.split(' ');
                                return (
                                  <button 
                                    key={idx}
                                    onClick={() => setSelectedSlot(day)}
                                    className={`p-1.5 rounded-lg flex flex-col items-center transition-all duration-200 ${
                                      selectedSlot === day ? 'bg-blue-600 text-white min-h-[42px]' : 'hover:bg-zinc-800/5 text-zinc-600 dark:text-zinc-400'
                                    }`}
                                  >
                                    <span className="text-[7px] uppercase font-bold tracking-tight">{dayName}</span>
                                    <span className="text-[10px] font-mono font-black mt-1">{dateNum}</span>
                                  </button>
                                );
                              })}
                            </div>

                            {/* Service packages list */}
                            <div className="w-[120px] space-y-1.5 shrink-0 text-left">
                              <div className="p-1 px-2 border rounded-lg text-[8px] font-bold bg-white dark:bg-zinc-900 shadow-sm" style={{ borderColor: 'var(--border-color)' }}>
                                <span>Discovery Call</span>
                                <span className="block font-mono text-zinc-550 font-bold mt-0.5">30 mins (Free)</span>
                              </div>
                              <div className="p-1 px-2 border rounded-lg text-[8px] font-bold bg-white dark:bg-zinc-900 shadow-sm" style={{ borderColor: 'var(--border-color)' }}>
                                <span>Design Workshop</span>
                                <span className="block font-mono text-zinc-550 font-bold mt-0.5">2 hrs ($120)</span>
                              </div>
                            </div>
                          </div>

                          {selectedSlot ? (
                            <div className="p-2 border rounded-lg text-emerald-500 bg-emerald-555/5 text-[9px] font-bold border-emerald-500/20 text-center animate-fade-in">
                              ✔ Slot locked for {selectedSlot}. Invite details automatically dispatched to user mailbox.
                            </div>
                          ) : (
                            <div className="p-2 border border-dashed rounded-lg text-zinc-400 text-[8px] font-mono text-center">
                              * Select a scheduling calendar node slot above to sync appointments
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── MOCK SCREEN CONTENT: BRAND SHOWCASE ── */}
                      {siteArchetype === 'brand' && (
                        <div className="space-y-4">
                          <div className="flex justify-between items-end border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
                            <div className="text-left">
                              <span className="text-[8px] font-mono uppercase tracking-widest text-zinc-400">STUDIO PORTFOLIO</span>
                              <h4 className="text-xs font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>Engineered Products Showcase</h4>
                            </div>
                            <div className="flex gap-2 text-[8px] font-bold text-zinc-500 select-none">
                              <span className="text-blue-500 underline">All Works</span>
                              <span>UIUX</span>
                              <span>Mobile</span>
                            </div>
                          </div>
                          
                          {/* Showcase Grid with Gradient representations */}
                          <div className="grid grid-cols-3 gap-2.5 pt-1">
                            <div className="aspect-square bg-gradient-to-tr from-cyan-600/35 to-blue-500/20 border rounded-lg flex flex-col justify-end p-2 relative overflow-hidden transition-all duration-300 hover:scale-105 group cursor-pointer"
                                 style={{ borderColor: 'var(--border-color)' }}>
                              <span className="text-[6.5px] uppercase font-mono font-black text-white tracking-widest leading-none">Nike Studio</span>
                              <span className="text-[5.5px] font-bold text-zinc-300 mt-0.5">Custom WebGL Showcase</span>
                            </div>

                            <div className="aspect-square bg-gradient-to-tr from-purple-600/35 to-pink-500/20 border rounded-lg flex flex-col justify-end p-2 relative overflow-hidden transition-all duration-300 hover:scale-105 group cursor-pointer"
                                 style={{ borderColor: 'var(--border-color)' }}>
                              <span className="text-[6.5px] uppercase font-mono font-black text-white tracking-widest leading-none">Stripe Checkout</span>
                              <span className="text-[5.5px] font-bold text-zinc-300 mt-0.5">Clean APIs Hub</span>
                            </div>

                            <div className="aspect-square bg-gradient-to-tr from-emerald-600/35 to-teal-500/20 border rounded-lg flex flex-col justify-end p-2 relative overflow-hidden transition-all duration-300 hover:scale-105 group cursor-pointer"
                                 style={{ borderColor: 'var(--border-color)' }}>
                              <span className="text-[6.5px] uppercase font-mono font-black text-white tracking-widest leading-none">Airbnb Map</span>
                              <span className="text-[5.5px] font-bold text-zinc-300 mt-0.5">React Hydration</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Add-on feature widget: Blog list */}
                      {addOnBlog && (
                        <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                          <span className="text-[8px] uppercase font-black tracking-wider text-blue-500 block mb-2 text-left">Latest Developer Insights (SEO Blog)</span>
                          <div className="flex gap-3 items-center">
                            <div className="h-10 w-16 bg-gradient-to-tr from-zinc-700 to-zinc-900 rounded-lg flex items-center justify-center shrink-0">
                              <span className="text-[7px] text-zinc-500 leading-none">POST COVERS</span>
                            </div>
                            <div className="text-left space-y-1">
                              <div className="h-3 w-40 bg-zinc-300 dark:bg-zinc-700 rounded" />
                              <div className="flex gap-2 text-[7px] text-zinc-400 font-bold">
                                <span>5 min read</span>
                                <span>•</span>
                                <span className="text-blue-500">By Lead Engineer</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>

                    {/* Add-on feature module: Chatbot bubble */}
                    {addOnChat && (
                      <div className="absolute bottom-4 right-4 flex items-center gap-2 select-none z-20 animate-bounce">
                        <div className="h-7 w-7 rounded-full bg-blue-600 shadow-md text-white flex items-center justify-center cursor-pointer">
                          <FaComments size={12} />
                        </div>
                        <div className="bg-white dark:bg-zinc-800 text-[8px] font-bold p-1.5 px-2.5 rounded-xl shadow border border-zinc-200 dark:border-zinc-700">
                          Hi there! Can we help?
                        </div>
                      </div>
                    )}

                  </div>
                </div>

                {/* Plain-English summary & value descriptions */}
                <div className="mt-6 p-4 rounded-xl border text-left" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                  <h4 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-primary)' }}>
                    Blueprint Specifications Analysis
                  </h4>
                  
                  {siteArchetype === 'ecommerce' && (
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                      This setup equips you with a secure marketplace database wired to accept credit card payments globally. Add customer portals to track client product receipts and billing history seamlessly.
                    </p>
                  )}
                  {siteArchetype === 'saas' && (
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                      Ideal for tech products, directories, or secure client portals. The dashboard supports user subscription states, and custom analytical tables fetching server state variables.
                    </p>
                  )}
                  {siteArchetype === 'booking' && (
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                      Increases service bookings by showing dynamic available slots. Once confirmed, automated email reminder hooks keep clients informed without manual administrator task cycles.
                    </p>
                  )}
                  {siteArchetype === 'brand' && (
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                      Creates a memorable high-end brand landing page. Focused on ultra-fast asset loads, custom SEO tags to index on search results, and clean visual graphics showcase modules.
                    </p>
                  )}
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── THE INTERACTIVE DEV PROCESS JOURNEY ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-card)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-5xl">
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Our Process Journey</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>The Journey from Concept to Launch</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Follow our engineering timeline interactively. Click each milestone of our development console to run compilation, tests, deployment layers, and monitoring.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Toggles Panel (5 columns) */}
            <div className="lg:col-span-5 flex flex-col justify-between">
              
              <div className="space-y-3">
                {/* 1. Designing */}
                <button 
                  onClick={() => setJourneyStage('designing')}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border text-left transition-all duration-300 ${
                    journeyStage === 'designing' ? 'bg-blue-600/10 border-blue-500/30 scale-[1.02]' : 'border-transparent hover:bg-zinc-800/10'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    journeyStage === 'designing' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    <FaPalette size={14} />
                  </div>
                  <div>
                    <h4 className="text-[13px] font-bold uppercase tracking-wide font-sans m-0" style={{ color: 'var(--text-primary)' }}>01 | Designing</h4>
                    <p className="text-xs text-zinc-500 mt-0.5">Wireframes & systematic design systems.</p>
                  </div>
                </button>

                {/* 2. Development */}
                <button 
                  onClick={() => setJourneyStage('development')}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border text-left transition-all duration-300 ${
                    journeyStage === 'development' ? 'bg-blue-600/10 border-blue-500/30 scale-[1.02]' : 'border-transparent hover:bg-zinc-800/10'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    journeyStage === 'development' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    <FaLaptopCode size={14} />
                  </div>
                  <div>
                    <h4 className="text-[13px] font-bold uppercase tracking-wide font-sans m-0" style={{ color: 'var(--text-primary)' }}>02 | Development</h4>
                    <p className="text-xs text-zinc-500 mt-0.5">Clean APIs, strict types, and robust code.</p>
                  </div>
                </button>

                {/* 3. Testing */}
                <button 
                  onClick={() => setJourneyStage('testing')}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border text-left transition-all duration-300 ${
                    journeyStage === 'testing' ? 'bg-blue-600/10 border-blue-500/30 scale-[1.02]' : 'border-transparent hover:bg-zinc-800/10'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    journeyStage === 'testing' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    <FaBug size={14} />
                  </div>
                  <div>
                    <h4 className="text-[13px] font-bold uppercase tracking-wide font-sans m-0" style={{ color: 'var(--text-primary)' }}>03 | Testing Suite</h4>
                    <p className="text-xs text-zinc-500 mt-0.5">Automated test pipelines for stability.</p>
                  </div>
                </button>

                {/* 4. Deployment */}
                <button 
                  onClick={() => setJourneyStage('deployment')}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border text-left transition-all duration-300 ${
                    journeyStage === 'deployment' ? 'bg-blue-600/10 border-blue-500/30 scale-[1.02]' : 'border-transparent hover:bg-zinc-800/10'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    journeyStage === 'deployment' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    <FaCloudUploadAlt size={14} />
                  </div>
                  <div>
                    <h4 className="text-[13px] font-bold uppercase tracking-wide font-sans m-0" style={{ color: 'var(--text-primary)' }}>04 | Edge Deploy</h4>
                    <p className="text-xs text-zinc-500 mt-0.5">Sync staging routes for quick feedback.</p>
                  </div>
                </button>

                {/* 5. Support */}
                <button 
                  onClick={() => setJourneyStage('support')}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border text-left transition-all duration-300 ${
                    journeyStage === 'support' ? 'bg-blue-600/10 border-blue-500/30 scale-[1.02]' : 'border-transparent hover:bg-zinc-800/10'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    journeyStage === 'support' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    <FaWrench size={14} />
                  </div>
                  <div>
                    <h4 className="text-[13px] font-bold uppercase tracking-wide font-sans m-0" style={{ color: 'var(--text-primary)' }}>05 | Support & Ops</h4>
                    <p className="text-xs text-zinc-505 mt-0.5">Uptime monitoring and auto alerts setup.</p>
                  </div>
                </button>
              </div>

            </div>

            {/* Immersive View Panel (7 columns) */}
            <div className="lg:col-span-7 flex flex-col justify-stretch">
              <div className="w-full h-full border rounded-3xl p-6 flex flex-col shadow-xl min-h-[360px]"
                   style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                
                {/* Phase Title */}
                <div className="flex items-center justify-between pb-3 border-b mb-6" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                    <span className="text-[10px] uppercase font-bold tracking-widest font-mono text-zinc-400">
                      Console Workspace Simulator
                    </span>
                  </div>
                  <span className="text-[9px] uppercase font-bold tracking-wider text-blue-500 border border-blue-505/30 px-2.5 py-1 rounded bg-blue-500/5">
                    {journeyStage}
                  </span>
                </div>

                {/* ── SUB-WIDGET 01: DESIGNING ── */}
                {journeyStage === 'designing' && (
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="text-sm font-bold mb-2 text-zinc-700 dark:text-zinc-300">Phase 01: System Tokens Layout</h4>
                      <p className="text-xs text-zinc-400 leading-relaxed max-w-md mb-4">
                        We write centralized styles (components, layout grids, spacing variables) before designing interfaces. Adjust values below to test rendering variations.
                      </p>

                      <div className="grid grid-cols-2 gap-4 my-6">
                        {/* Spacing selector */}
                        <div>
                          <label className="text-[10px] uppercase font-bold text-zinc-400 font-mono tracking-wider block mb-2">Spacing Token</label>
                          <div className="flex gap-2">
                            {['tight', 'cozy', 'loose'].map((sp) => (
                              <button 
                                key={sp} 
                                onClick={() => setDesignSpacing(sp)}
                                className={`flex-grow py-1.5 px-3 text-[10px] font-bold font-mono border rounded-lg transition-all duration-200 capitalize ${
                                  designSpacing === sp ? 'bg-blue-600/15 text-blue-500 border-blue-505/35' : 'bg-transparent text-zinc-500'
                                }`}
                              >
                                {sp}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Theme selector */}
                        <div>
                          <label className="text-[10px] uppercase font-bold text-zinc-400 font-mono tracking-wider block mb-2">Primary Hue</label>
                          <div className="flex gap-2">
                            {['cyan', 'emerald', 'indigo'].map((th) => (
                              <button 
                                key={th} 
                                onClick={() => setDesignTheme(th)}
                                className={`flex-grow py-1.5 px-3 text-[10px] font-bold font-mono border rounded-lg transition-all duration-200 capitalize ${
                                  designTheme === th ? 'bg-blue-600/15 text-blue-500 border-blue-505/35' : 'bg-transparent text-zinc-505'
                                }`}
                              >
                                {th}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Previews container */}
                    <div className="p-4 rounded-2xl border flex items-center justify-center min-h-[90px] transition-all duration-300"
                         style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                      <div className="flex gap-2 items-center w-full max-w-sm justify-center">
                        <div className={`p-3 rounded-xl border text-[11px] font-bold transition-all duration-300 shrink-0 ${
                          designTheme === 'cyan' ? 'border-cyan-500/30 text-cyan-500 bg-cyan-500/5' : 
                          designTheme === 'emerald' ? 'border-emerald-500/30 text-emerald-500 bg-emerald-500/5' :
                          'border-indigo-500/30 text-indigo-500 bg-indigo-500/5'
                        }`}>
                          Token Card
                        </div>
                        <div className={`h-1.5 rounded-full transition-all duration-300 ${
                          designSpacing === 'tight' ? 'w-8' : designSpacing === 'cozy' ? 'w-20' : 'w-36'
                        } ${
                          designTheme === 'cyan' ? 'bg-cyan-500' : 
                          designTheme === 'emerald' ? 'bg-emerald-500' :
                          'bg-indigo-500'
                        }`} />
                        <div className={`h-6 w-6 rounded-full transition-all duration-300 shrink-0 ${
                          designTheme === 'cyan' ? 'bg-cyan-500/25 border-cyan-505' : 
                          designTheme === 'emerald' ? 'bg-emerald-500/25 border-emerald-505' :
                          'bg-indigo-500/25 border-indigo-505'
                        } border`} />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── SUB-WIDGET 02: DEVELOPMENT ── */}
                {journeyStage === 'development' && (
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="text-sm font-bold mb-2 text-zinc-700 dark:text-zinc-300">Phase 02: Production Compilation</h4>
                      <p className="text-xs text-zinc-400 leading-relaxed max-w-md mb-2">
                        We write TypeScript components and compile them using advanced assets bundling pipelines. Tap compile to trigger the bundler tool.
                      </p>
                    </div>

                    {/* Console Code Editor */}
                    <div className="flex-1 rounded-2xl p-4 border font-mono text-[10px] flex flex-col justify-between my-4"
                         style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                      <div className="text-left whitespace-pre select-none font-bold text-zinc-505 shrink-0">
                        <span className="text-blue-505">import</span> &#123; db &#125; <span className="text-blue-505">from</span> <span className="text-emerald-505">"@lib/database"</span>;
                        <br />
                        <span className="text-purple-505">export async function</span> <span className="text-amber-505">getUserData</span>(id: string) &#123;
                        <br />
                        &nbsp;&nbsp;<span className="text-blue-505">return await</span> db.user.findUnique(&#123; where: &#123; id &#125; &#125;);
                        <br />
                        &#125;
                      </div>
                      
                      {devOutput && (
                        <div className="border-t pt-3 mt-3 text-left text-zinc-400 font-mono overflow-y-auto max-h-[80px]">
                          {devOutput}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button 
                        onClick={handleCompileDev}
                        disabled={devCompiling || devCompiled}
                        className="btn-primary hover:scale-100 flex items-center justify-center gap-2 py-2 px-6 rounded-xl text-xs"
                      >
                        {devCompiling ? (
                          <>
                            <span className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                            COMPILING MODULES...
                          </>
                        ) : devCompiled ? (
                          <>
                            <FaCheck /> BUNDLING RESOLVED
                          </>
                        ) : (
                          <>
                            <FaPlay size={9} /> RUN DEVELOPMENT BUILD
                          </>
                        )}
                      </button>
                      
                      {devCompiled && (
                        <button 
                          onClick={resetDevMode}
                          className="px-4 py-2 border rounded-xl font-bold font-mono text-zinc-400 hover:text-zinc-300 border-zinc-700/50 hover:bg-zinc-800/20 text-xs"
                        >
                          RESET
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── SUB-WIDGET 03: TESTING ── */}
                {journeyStage === 'testing' && (
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="text-sm font-bold mb-2 text-zinc-700 dark:text-zinc-300">Phase 03: Automated Vitest Checkpoints</h4>
                      <p className="text-xs text-zinc-400 leading-relaxed max-w-md mb-2">
                        We write programmatic integration tests. Tapping verification triggers dynamic validation blocks.
                      </p>
                    </div>

                    {/* Test logs output */}
                    <div className="flex-1 rounded-2xl p-4 border font-mono text-[10px] text-zinc-400 text-left overflow-y-auto my-4 max-h-[140px]"
                         style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                      {testLogs.length === 0 ? (
                        <div className="text-zinc-550 font-bold block text-center mt-6">--- Console output empty. Tap Run Test Suite to initialize ---</div>
                      ) : (
                        testLogs.map((log, idx) => (
                          <div 
                            key={idx} 
                            className={`mb-1.5 whitespace-pre-wrap font-bold ${
                              log.includes('✓') ? 'text-emerald-500' : log.includes('passed') ? 'text-emerald-400 bg-emerald-505/5 p-2 rounded-lg' : 'text-zinc-505'
                            }`}
                          >
                            {log}
                          </div>
                        ))
                      )}
                    </div>

                    <button 
                      onClick={handleRunTests}
                      disabled={testsRunning}
                      className="btn-primary hover:scale-100 flex items-center justify-center gap-2 py-2 px-6 rounded-xl text-xs self-start"
                    >
                      {testsRunning ? (
                        <>
                          <span className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                          RUNNING PIPELINE SUITES...
                        </>
                      ) : testsPassed ? (
                        <>
                          <FaCheck /> ALL PIPELINES PASSING
                        </>
                      ) : (
                        <>
                          <FaPlay size={9} /> RUN AUTOMATED TEST SUITE
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* ── SUB-WIDGET 04: DEPLOYMENT ── */}
                {journeyStage === 'deployment' && (
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="text-sm font-bold mb-2 text-zinc-700 dark:text-zinc-300">Phase 04: Edge Pipeline Push</h4>
                      <p className="text-xs text-zinc-400 leading-relaxed max-w-md mb-2">
                        We configure continuous delivery protocols. Hit deployment to sync local builds dynamically on CDN nodes.
                      </p>
                    </div>

                    {/* Deploy console output */}
                    <div className="flex-1 rounded-2xl p-4 border font-mono text-[10px] text-zinc-405 text-left overflow-y-auto my-4 max-h-[140px]"
                         style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                      {deployLogs.length === 0 ? (
                        <div className="text-zinc-550 font-bold block text-center mt-6">--- Pipeline offline. Tap Sync Edge to start build ---</div>
                      ) : (
                        deployLogs.map((log, idx) => (
                          <div 
                            key={idx} 
                            className={`mb-1.5 whitespace-pre-wrap font-bold ${
                              log.includes('✔') ? 'text-emerald-500' : log.includes('[Pipeline]') ? 'text-blue-500' : 'text-zinc-505'
                            }`}
                          >
                            {log}
                          </div>
                        ))
                      )}
                    </div>

                    <div className="flex gap-2 items-center">
                      <button 
                        onClick={handleDeployToStaging}
                        disabled={deploying}
                        className="btn-primary hover:scale-100 flex items-center justify-center gap-2 py-2 px-6 rounded-xl text-xs self-start"
                      >
                        {deploying ? (
                          <>
                            <span className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                            DEPLOYING BUILDS...
                          </>
                        ) : deployed ? (
                          <>
                            <FaCheck /> COMPILE ENDPOINTS DEPLOYED
                          </>
                        ) : (
                          <>
                            <FaPlay size={9} /> DEPLOY TO EDGE STAGING
                          </>
                        )}
                      </button>
                      
                      {deployed && (
                        <a 
                          href="https://staging.bccweb.dev" 
                          target="_blank" 
                          rel="noreferrer"
                          className="flex items-center gap-1.5 px-3 py-2 border rounded-xl text-xs font-bold font-mono text-blue-500 border-blue-505/20 hover:bg-blue-500/5 transition-all duration-200"
                        >
                          <FaLink size={10} /> Test URL
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* ── SUB-WIDGET 05: SUPPORT & OPS ── */}
                {journeyStage === 'support' && (
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="text-sm font-bold mb-2 text-zinc-700 dark:text-zinc-300">Phase 05: Active Telemetry & Support</h4>
                      <p className="text-xs text-zinc-400 leading-relaxed max-w-md mb-2">
                        We monitor deployment server pools and set automatic failure alert thresholds in real-time.
                      </p>
                    </div>

                    {/* Telemetry charts meters */}
                    <div className="grid grid-cols-3 gap-3 my-4">
                      {/* Active server CPU load */}
                      <div className="p-3 border rounded-xl text-center" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                        <span className="text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">Server CPU</span>
                        <span className="font-mono text-lg font-black text-blue-500">{telemetryServerLoad}%</span>
                      </div>
                      
                      {/* Memory load */}
                      <div className="p-3 border rounded-xl text-center" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                        <span className="text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">Memory Allocation</span>
                        <span className="font-mono text-lg font-black text-amber-500">{telemetryMemory} MB</span>
                      </div>

                      {/* Uptime */}
                      <div className="p-3 border rounded-xl text-center" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                        <span className="text-[9px] uppercase tracking-wider text-zinc-550 block mb-1">SLA Uptime</span>
                        <span className="font-mono text-lg font-black text-emerald-500">{telemetryUptime}%</span>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl border flex items-center justify-between" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                        <span className="text-[10px] font-mono font-bold text-zinc-500">TELEMETRY PIPELINE MONITORING ACTIVE</span>
                      </div>
                      <span className="text-[10px] font-mono font-black text-emerald-500 border border-emerald-500/20 px-2 py-0.5 rounded bg-emerald-500/5">SSL SECURE</span>
                    </div>
                  </div>
                )}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── BACK TO ALL SERVICES LINK ── */}
      <section className="py-20 text-center" style={{ background: 'var(--bg-primary)' }}>
        <Link to="/services" className="text-sm font-semibold transition-colors inline-flex items-center gap-2"
              style={{ color: 'var(--accent)' }}>
          <FaArrowRight size={12} className="transform rotate-180" /> Back to All Services
        </Link>
      </section>

    </div>
  );
}
